'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ArrowLeft,
  DownloadSimple,
  Play,
  Prohibit,
  Trophy,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDocumentTitle } from '@/lib/use-document-title';
import type {
  ArenaExperiment,
  ArenaVariant,
  ArenaModality,
  ArenaExperimentStatus,
  ArenaGenerationStatus,
  ArenaGenerationView,
  BlindGenerationView,
} from '@/lib/arena/types';
import { ModalityBadge, StatusChip, SpendMeter, GenStatusChip } from '@/components/arena/badges';
import { GenerationMedia, CardMetrics, ZoomOverlay } from '@/components/arena/generation-media';
import { ReviewCanvas } from '@/components/arena/review-canvas';
import { LeaderboardTab } from '@/components/arena/leaderboard-tab';

interface Detail {
  experiment: ArenaExperiment;
  variants: ArenaVariant[];
  generationCounts: Partial<Record<ArenaGenerationStatus, number>>;
}

const RUNNABLE_STATUSES: ArenaExperimentStatus[] = ['draft', 'completed', 'failed', 'budget_exhausted', 'canceled'];

export function ExperimentDetailClient({ experimentId }: { experimentId: string }) {
  const router = useRouter();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('overview');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useDocumentTitle(detail?.experiment.name ? `${detail.experiment.name} · Arena` : 'Arena');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/arena/experiments/${experimentId}`);
      if (res.status === 404) throw new Error('Experiment not found');
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const data = (await res.json()) as Detail;
      setDetail(data);
      setError(null);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
      return null;
    } finally {
      setLoading(false);
    }
  }, [experimentId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while running.
  useEffect(() => {
    const running = detail?.experiment.status === 'running';
    if (running && !pollRef.current) {
      pollRef.current = setInterval(() => void load(), 3000);
    } else if (!running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [detail?.experiment.status, load]);

  const setStatus = (status: ArenaExperimentStatus) =>
    setDetail((d) => (d ? { ...d, experiment: { ...d.experiment, status } } : d));

  const run = async () => {
    setBusy(true);
    const prev = detail?.experiment.status as ArenaExperimentStatus | undefined;
    setStatus('running'); // optimistic
    try {
      const res = await fetch(`/api/arena/experiments/${experimentId}/run`, { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (res.status === 202) {
        toast.success('Run started');
        void load();
      } else {
        throw new Error(typeof data?.error === 'string' ? data.error : `Run failed (${res.status})`);
      }
    } catch (err) {
      if (prev) setStatus(prev);
      toast.error(err instanceof Error ? err.message : 'Run failed');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    const prev = detail?.experiment.status as ArenaExperimentStatus | undefined;
    setStatus('canceled');
    try {
      const res = await fetch(`/api/arena/experiments/${experimentId}/cancel`, { method: 'POST' });
      if (!res.ok) throw new Error(`Cancel failed (${res.status})`);
      toast.success('Experiment canceled');
      void load();
    } catch (err) {
      if (prev) setStatus(prev);
      toast.error(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen animate-pulse bg-background p-6" />;
  }
  if (error || !detail) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-center">
        <p className="text-sm font-semibold">{error ?? 'Not found'}</p>
        <Button variant="outline" onClick={() => router.push('/arena')}>
          Back to Arena
        </Button>
      </div>
    );
  }

  const { experiment, variants } = detail;
  const status = experiment.status as ArenaExperimentStatus;
  const isRunning = status === 'running';
  const canRun = RUNNABLE_STATUSES.includes(status);

  return (
    <div className="min-h-screen bg-background p-2 md:p-3">
      <Toaster position="top-center" richColors closeButton />
      <div className="relative flex min-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-border/80 bg-card/40">
        <header className="sticky top-0 z-30 flex flex-wrap items-center gap-3 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md md:px-6">
          <button
            type="button"
            onClick={() => router.push('/arena')}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Back"
          >
            <ArrowLeft weight="bold" className="size-4" />
          </button>
          <Trophy weight="duotone" className="size-5 text-amber-400" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-sm font-extrabold tracking-tight">{experiment.name}</h1>
              <StatusChip status={status} />
              <ModalityBadge modality={experiment.modality as ArenaModality} />
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden w-40 md:block">
              <SpendMeter spent={experiment.costSpent} cap={experiment.budgetCapCredits} />
            </div>
            <a
              href={`/api/arena/experiments/${experimentId}/export?format=json`}
              className="inline-flex h-8 items-center gap-1 rounded-full border border-border/60 px-3 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <DownloadSimple weight="bold" className="size-3.5" /> JSON
            </a>
            <a
              href={`/api/arena/experiments/${experimentId}/export?format=csv`}
              className="inline-flex h-8 items-center gap-1 rounded-full border border-border/60 px-3 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <DownloadSimple weight="bold" className="size-3.5" /> CSV
            </a>
            {isRunning ? (
              <Button
                size="sm"
                variant="outline"
                className="h-8 rounded-full text-xs"
                disabled={busy}
                onClick={() => void cancel()}
              >
                <Prohibit weight="bold" className="mr-1 size-3.5" /> Cancel
              </Button>
            ) : (
              <Button
                size="sm"
                className="h-8 rounded-full bg-violet-500 px-4 text-xs font-extrabold text-white hover:bg-violet-400"
                disabled={busy || !canRun}
                onClick={() => void run()}
              >
                <Play weight="bold" className="mr-1 size-3.5" /> Run
              </Button>
            )}
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-5 md:px-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="review">Review</TabsTrigger>
              <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="pt-4">
              <OverviewTab experimentId={experimentId} experiment={experiment} variants={variants} refreshKey={status} />
            </TabsContent>

            <TabsContent value="review" className="pt-4">
              {tab === 'review' && (
                <ReviewTab experimentId={experimentId} modality={experiment.modality as ArenaModality} />
              )}
            </TabsContent>

            <TabsContent value="leaderboard" className="pt-4">
              {tab === 'leaderboard' && <LeaderboardTab experimentId={experimentId} />}
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Overview — variants table + generations grid grouped by variant (non-blind).
// --------------------------------------------------------------------------
function OverviewTab({
  experimentId,
  experiment,
  variants,
  refreshKey,
}: {
  experimentId: string;
  experiment: ArenaExperiment;
  variants: ArenaVariant[];
  refreshKey: string;
}) {
  const [gens, setGens] = useState<ArenaGenerationView[]>([]);
  const [zoom, setZoom] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/arena/experiments/${experimentId}/generations?pageSize=200`);
      if (!res.ok) return;
      const data = await res.json();
      setGens(Array.isArray(data.generations) ? data.generations : []);
    } catch {
      /* non-fatal */
    }
  }, [experimentId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const byVariant = new Map<string, ArenaGenerationView[]>();
  for (const g of gens) {
    const arr = byVariant.get(g.variantId) ?? [];
    arr.push(g);
    byVariant.set(g.variantId, arr);
  }

  return (
    <div className="space-y-6">
      <ZoomOverlay url={zoom} onClose={() => setZoom(null)} />

      {experiment.description && (
        <p className="text-sm text-muted-foreground">{experiment.description}</p>
      )}

      {/* Variants table */}
      <div className="overflow-x-auto rounded-2xl border border-border/60">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/60 bg-muted/30 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-semibold">Variant</th>
              <th className="px-3 py-2 font-semibold">Kind</th>
              <th className="px-3 py-2 font-semibold">Model</th>
              <th className="px-3 py-2 font-semibold">Provider</th>
            </tr>
          </thead>
          <tbody>
            {variants.map((v) => (
              <tr key={v.id} className="border-b border-border/40 last:border-0">
                <td className="px-3 py-2 font-semibold">{v.label}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{v.generatorKind}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{v.model ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{v.provider ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Generations grouped by variant */}
      {variants.map((v) => {
        const list = byVariant.get(v.id) ?? [];
        return (
          <div key={v.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-bold">{v.label}</h3>
              <span className="text-[10px] text-muted-foreground">
                {v.model ?? v.generatorKind} · {list.length} gens
              </span>
            </div>
            {list.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">No generations yet.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                {list
                  .sort((a, b) => a.sampleIndex - b.sampleIndex)
                  .map((g) => (
                    <div key={g.id} className="flex flex-col gap-1.5 rounded-2xl border border-border/60 bg-card/40 p-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">#{g.sampleIndex}</span>
                        <GenStatusChip status={g.status} />
                      </div>
                      <GenerationMedia gen={g} onZoom={setZoom} />
                      {g.status === 'failed' && g.error && (
                        <p className="line-clamp-2 text-[10px] text-red-400" title={g.error}>
                          {g.error}
                        </p>
                      )}
                      <CardMetrics gen={g} />
                    </div>
                  ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// --------------------------------------------------------------------------
// Review — BLIND only. Fetches with blindMode=true; ReviewCanvas is typed to
// BlindGenerationView so identity fields cannot leak in.
// --------------------------------------------------------------------------
function ReviewTab({ experimentId, modality }: { experimentId: string; modality: ArenaModality }) {
  const [gens, setGens] = useState<BlindGenerationView[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/arena/experiments/${experimentId}/generations?blindMode=true&status=succeeded&pageSize=200`,
        );
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (!cancelled) setGens(Array.isArray(data.generations) ? data.generations : []);
      } catch {
        if (!cancelled) setGens([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [experimentId]);

  if (gens === null) {
    return <div className="min-h-[280px] animate-pulse rounded-2xl border border-border/60 bg-card/40" />;
  }
  return <ReviewCanvas experimentId={experimentId} modality={modality} generations={gens} />;
}
